// Canonical single-address email rule. MUST stay identical to validation.ts
// (singleEmailRegex) and the backend computeValidationState in
// gst-backend/serverless.yml. Exactly one address, NO comma, TLD >= 2 letters.
const emailRegex = /^[^\s@,]+@[^\s@,]+\.[A-Za-z]{2,}$/;
const alphaNumericRegex = /[a-zA-Z0-9\-_]/;

export const validateCustomer = (customer, customers, setFormValidationState) => {
  customer.valid = true;
  
  const emptyValidationState = {
    primaryEmail: "",
    secondaryEmail: [],
    customerNr: "",
    organization: "",
    company: "",
    company2: [],
    country: "",
    language: "",
    telephone: [],
    firstName: "",
    surName: "",
    valid: true
  };
  
  setFormValidationState(emptyValidationState);

  if (validateCustomerNr(customer.customerNr, setFormValidationState)) {
    customer.valid = false;
  }

  if (validatePrimaryEmail(customer, customers, setFormValidationState)) {
    customer.valid = false;
  }

  if (validateSecondaryEmail(customer, customers, setFormValidationState).length > 0) {
    customer.valid = false;
  }

  if (validateCompany(customer.company, setFormValidationState)) {
    customer.valid = false;
  }

  if (validateCompany2(customer.company2, setFormValidationState).length > 0) {
    customer.valid = false;
  }

  if (validateCountry(customer.country, setFormValidationState)) {
    customer.valid = false;
  }

  if (validateLanguage(customer.language, setFormValidationState)) {
    customer.valid = false;
  }

  if (validateTelephone(customer.telephone, setFormValidationState).length > 0) {
    customer.valid = false;
  }

  if (validateFirstName(customer.firstName, setFormValidationState)) {
    customer.valid = false;
  }

  if (validateSurName(customer.surName, setFormValidationState)) {
    customer.valid = false;
  }

  return customer.valid;
};

export const validateCountry = (country, setFormValidationState) => {
  const countryRegex = /^A[^ABCHJKNPVY ]|B[^CKPUX ]|C[^BEJPQST ]|D[EJKMOZ]|E[CEGHRST]|F[IJKMOR]|G[^CJKOVXZ ]|H[KMNRTU]|I[DEL-OQ-T]|J[EMOP]|K[EGHIMNPRWYZ]|L[ABCIKR-VY]|M[^BIJ ]|N[ACEFGILOPRUZ ]|OM|P[AE-HK-NRSTWY]|QA|R[EOSUW]|S[^FPQUW ]|T[^ABEIPQSUXY ]|U[AGMSYZ]|V[ACEGINU]|WF|WS|YE|YT|Z[AMW]$/;
  let errorMessage = "";
  if (!countryRegex.test(country)) {
    errorMessage = "Country format invalid! (AA)";
    setFormValidationState((state) => ({ ...state, valid: false }));
  }
  setFormValidationState((state) => ({ ...state, country: errorMessage }));
  return errorMessage;
};

export const validateCustomerNr = (customerNr, setFormValidationState) => {
  let errorMessage = "";
  if (!customerNr.trim()) {
    errorMessage = "CustomerNr is required!";
    setFormValidationState((state) => ({ ...state, valid: false }));
  } else if (customerNr.length > 50) {
    errorMessage = "CustomerNr must have max. 50 characters!";
    setFormValidationState((state) => ({ ...state, valid: false }));
  } else if (!alphaNumericRegex.test(customerNr)) {
    errorMessage = "CustomerNr must be alphanumeric!";
    setFormValidationState((state) => ({ ...state, valid: false }));
  }
  setFormValidationState((state) => ({ ...state, customerNr: errorMessage }));
  return errorMessage;
};

export const validatePrimaryEmail = (_customer, customers, setFormValidationState) => {
  let errorMessage = "";
  if (!emailRegex.test((_customer.primaryEmail || '').trim())) {
    errorMessage = "Email format invalid!";
    setFormValidationState((state) => ({ ...state, valid: false }));
  } else {
    errorMessage = checkDuplicates(_customer.primaryEmail, _customer, customers, setFormValidationState).toString();
  }
  setFormValidationState((state) => ({ ...state, primaryEmail: errorMessage }));
  return errorMessage;
};

export const validateSecondaryEmail = (_customer, customers, setFormValidationState) => {
  let errors = [];
  const emails = _customer.secondaryEmail;
  
  // Handle null, undefined, or empty array
  if (!emails || !Array.isArray(emails) || emails.length === 0) {
    return errors;
  }
  
  for (var i = 0; i < emails.length; i++) {
    let errorMessage = "";
    if (!emailRegex.test((emails[i] || '').trim())) {
      errorMessage = "Email format invalid! ";
      errors.push(errorMessage + " (" + emails[i] + ")");
      setFormValidationState((state) => ({ ...state, valid: false }));
    } else {
      errors = [...checkDuplicates(emails[i], _customer, customers, setFormValidationState)];
    }
  }
  if (errors.length > 0) {
    setFormValidationState((state) => ({ ...state, secondaryEmail: errors }));
  }
  return errors;
};

export const checkDuplicates = (email, _customer, customers, setFormValidationState) => {
  let errorMessage = "";
  let errors = [];
  let index = findDuplicateEmailInPrimaryEmails(email, _customer, customers);
  if (index >= 0) {
    errorMessage = `"Email is possible duplicate of customer: ${customers[index].customerNr} (${customers[index].primaryEmail})"`;
    errors.push(errorMessage + " (" + email + ")");
    setFormValidationState((state) => ({ ...state, valid: false }));
  } else {
    index = findDuplicateEmailInSecondaryEmails(email, _customer, customers);
    if (index >= 0 && customers[index].customerNr !== _customer.customerNr) {
      errorMessage = `"Email is possible duplicate of customer: ${customers[index].customerNr} (${customers[index].secondaryEmail})"`;
      errors.push(errorMessage + " (" + email + ")");
      setFormValidationState((state) => ({ ...state, valid: false }));
    }
  }
  return errors;
};

export const validateCompany = (company, setFormValidationState) => {
  let errorMessage = "";
  if (!company.trim()) {
    errorMessage = "Company is required!";
    setFormValidationState((state) => ({ ...state, valid: false }));
  } else if (company.includes(',')) {
    errorMessage = "Company must be a single value! Use Additional Company Names for more.";
    setFormValidationState((state) => ({ ...state, valid: false }));
  } else if (company.length > 50) {
    errorMessage = "Company must have max. 50 characters!";
    setFormValidationState((state) => ({ ...state, valid: false }));
  } else if (!alphaNumericRegex.test(company)) {
    errorMessage = "Company must be alphanumeric!";
    setFormValidationState((state) => ({ ...state, valid: false }));
  }
  setFormValidationState((state) => ({ ...state, company: errorMessage }));
  return errorMessage;
};

export const validateCompany2 = (value, setFormValidationState) => {
  let errors = [];
  
  // Handle null, undefined, or empty array
  if (!value || !Array.isArray(value) || value.length === 0) {
    return errors;
  }
  
  for (var i = 0; i < value.length; i++) {
    let errorMessage = "";
    if (value[i].length > 50) {
      errorMessage = "Company must have max. 50 characters!";
      setFormValidationState((state) => ({ ...state, valid: false }));
      errors.push(errorMessage + " (" + value[i] + ")");
    } else if (!alphaNumericRegex.test(value[i])) {
      errorMessage = "Company2 must be alphanumeric!";
      setFormValidationState((state) => ({ ...state, valid: false }));
      errors.push(errorMessage + " (" + value[i] + ")");
    }
  }
  if (errors.length > 0) {
    setFormValidationState((state) => ({ ...state, company2: errors }));
  }
  return errors;
};

export const validateLanguage = (language, setFormValidationState) => {
  let errorMessage = "";
  const languageRegex = /^[a-z]{2}$/;
  if (!languageRegex.test(language)) {
    errorMessage = "Language format invalid! (aa)";
    setFormValidationState((state) => ({ ...state, valid: false }));
  }
  setFormValidationState((state) => ({ ...state, language: errorMessage }));
  return errorMessage;
};

export const validateTelephone = (telephone, setFormValidationState) => {
  let errors = [];
  
  // Handle null, undefined, or empty array
  if (!telephone || !Array.isArray(telephone) || telephone.length === 0) {
    return errors;
  }
  
  for (var i = 0; i < telephone.length; i++) {
    const errorMessage = validatePhoneNumber(telephone[i]);
    if (errorMessage) {
      errors.push(errorMessage);
      setFormValidationState((state) => ({ ...state, valid: false }));
    }
  }
  if (errors.length > 0) {
    setFormValidationState((state) => ({ ...state, telephone: errors }));
  }
  return errors;
};

export const validatePhoneNumber = (phoneNumber) => {
  const phoneRegex = /^\+[0-9]{5,15}$/;
  if (!phoneRegex.test(phoneNumber)) {
    return `"Telephone format invalid: ${phoneNumber} "`;
  }
  return "";
};

export const validateFirstName = (firstName, setFormValidationState) => {
  let errorMessage = "";
  if (firstName.length > 30) {
    errorMessage = "Firstname must have max. 30 characters!";
    setFormValidationState((state) => ({ ...state, valid: false }));
  } else if (firstName && !alphaNumericRegex.test(firstName)) {
    errorMessage = "Firstname must be alphanumeric!";
    setFormValidationState((state) => ({ ...state, valid: false }));
  }
  setFormValidationState((state) => ({ ...state, firstName: errorMessage }));
  return errorMessage;
};

export const validateSurName = (surName, setFormValidationState) => {
  let errorMessage = "";
  if (!surName.trim()) {
    errorMessage = "Surname is required!";
    setFormValidationState((state) => ({ ...state, valid: false }));
  } else if (surName.length > 50) {
    errorMessage = "Surname must have max. 30 characters!";
    setFormValidationState((state) => ({ ...state, valid: false }));
  } else if (!alphaNumericRegex.test(surName)) {
    errorMessage = "Surname must be alphanumeric!";
    setFormValidationState((state) => ({ ...state, valid: false }));
  }
  setFormValidationState((state) => ({ ...state, surName: errorMessage }));
  return errorMessage;
};

export const findDuplicateEmailInPrimaryEmails = (email, _customer, customers) => {
  let index = -1;
  index = customers.findIndex(item => (item.primaryEmail === email) && (item.customerNr !== _customer.customerNr));
  return index;
};

export const findDuplicateEmailInSecondaryEmails = (email, _customer, customers) => {
  let index = -1;
  index = customers.findIndex(item => item.secondaryEmail.includes(email) && item.customerNr !== _customer.customerNr);
  return index;
};
